// Dependency-free canvas confetti. Fires a celebratory burst and cleans up after itself.
// Respects prefers-reduced-motion (no-op).

const COLORS = ['#FF7A1A', '#7B5BFF', '#12B8A0', '#FF5470', '#FFC42E', '#36A9F0'];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {{x?:number,y?:number,count?:number,spread?:number,power?:number}} opts
 * x,y are viewport coordinates (defaults to upper-center).
 */
export function burst(opts = {}) {
  if (prefersReducedMotion()) return;

  const {
    x = window.innerWidth / 2,
    y = window.innerHeight * 0.38,
    count = 90,
    spread = Math.PI, // full upward fan
    power = 11,
  } = opts;

  const canvas = document.createElement('canvas');
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:9999;';
  canvas.width = window.innerWidth * dpr;
  canvas.height = window.innerHeight * dpr;
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  const particles = Array.from({ length: count }, () => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * spread;
    const velocity = power * (0.55 + Math.random() * 0.8);
    return {
      x,
      y,
      vx: Math.cos(angle) * velocity,
      vy: Math.sin(angle) * velocity - 3,
      size: 6 + Math.random() * 8,
      color: COLORS[(Math.random() * COLORS.length) | 0],
      rot: Math.random() * Math.PI * 2,
      vrot: (Math.random() - 0.5) * 0.4,
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
      life: 1,
      drag: 0.985 + Math.random() * 0.01,
    };
  });

  const gravity = 0.32;
  let raf;
  const start = performance.now();

  const frame = (now) => {
    const elapsed = now - start;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let alive = false;

    for (const p of particles) {
      p.vx *= p.drag;
      p.vy = p.vy * p.drag + gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vrot;
      if (elapsed > 1100) p.life -= 0.04;

      if (p.life > 0 && p.y < window.innerHeight + 40) {
        alive = true;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    }

    if (alive && elapsed < 2600) {
      raf = requestAnimationFrame(frame);
    } else {
      cancelAnimationFrame(raf);
      canvas.remove();
    }
  };

  raf = requestAnimationFrame(frame);
}

/** A floating "+N" score pop at a screen point. */
export function scorePop(value, x, y) {
  if (prefersReducedMotion() || !value) return;
  const el = document.createElement('div');
  el.className = 'sg-scorepop';
  el.textContent = `+${value}`;
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
  el.style.transform = 'translate(-50%, -50%)';
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 1200);
}
